import { useMutation, useQueryClient } from '@tanstack/react-query'
import { commentSchema } from '@witty-board/shared'
import { apiRequest, apiRequestVoid } from './client.js'
import { taskDetailQueryKey } from './tasks.js'

/**
 * Comentarios y actividad se piden juntos en `GET /tasks/{id}` (taskDetailSchema),
 * así que las tres mutaciones invalidan esa consulta en vez de tocar la caché a
 * mano: cada comentario también genera una fila de `activity` en el backend, y
 * duplicar esa lógica en el cliente no aporta nada frente a un simple refetch.
 */
export function useCreateCommentMutation(taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (body: string) =>
      apiRequest(`/tasks/${taskId}/comments`, commentSchema, { method: 'POST', body: { body } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskDetailQueryKey(taskId) })
      // `commentCount` de la tarjeta del tablero también cambia.
      void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
    },
  })
}

export function useUpdateCommentMutation(taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: string }) =>
      apiRequest(`/comments/${id}`, commentSchema, { method: 'PATCH', body: { body } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskDetailQueryKey(taskId) })
    },
  })
}

export function useDeleteCommentMutation(taskId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => apiRequestVoid(`/comments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskDetailQueryKey(taskId) })
      void queryClient.invalidateQueries({ queryKey: ['tasks'], exact: false })
    },
  })
}
